"use client";

import { Button, Carousel, Segmented, Spin, Image } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import caretRight from "assets/svg/CaretRight.svg";
// import seen from "assets/svg/seen.svg";

import styles from "./index.module.css";
import { CustomButton } from "Components/CustomButton";
import ArrowsClockwise from "assets/svg/ArrowsClockwise.svg";
import DownloadSimple from "assets/svg/DownloadSimple.svg";
import { replacePhoto } from "helpers/backend_helper";
import { ProductStatusEnum } from "helpers/types";
import { HEIC_ACCEPT } from "helpers/constants";
import { AdminContext } from "context/adminContext";

type Photo = {
  original: string;
  path: string;
  // можно добавить filename?: string; если хочешь кастомные имена файлов
};

type Props = {
  photos: Photo[];
  info: {
    id: string;
    version: "current" | "draft";
    status: ProductStatusEnum;
  };
  onReplacePhoto: ({
    index,
    isDraft,
    original,
    path,
  }: {
    original: string;
    path: string;
    index: number;
    isDraft: boolean;
  }) => void;
};

export const CarouselComponent = ({ photos: initialPhotos, info, onReplacePhoto }: Props) => {
  console.log(info);
  const [photos, setPhotos] = useState(initialPhotos); // mutable, чтобы менять при замене
  const [selected, setSelected] = useState("0");
  // const [viewed, setViewed] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { localCountry } = useContext(AdminContext);

  const slider = useRef<any>(null); // any, т.к. CarouselRef иногда глючит с типами
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentIndex = Number(selected);
  const currentPhoto = photos[currentIndex]?.original;

  const onChange = (value: string) => {
    setSelected(value);
  };

  useEffect(() => {
    setPhotos(initialPhotos);
  }, [initialPhotos]);

  useEffect(() => {
    slider.current?.goTo(Number(selected));

    // Отмечаем как viewed только после смены (не текущий при монтировании)
    // setViewed((prev) => {
    //   if (prev.includes(selected)) return prev;
    //   return [...prev, selected];
    // });
  }, [selected]);

  // Надёжное скачивание без открытия вкладки
  const handleDownload = async () => {
    if (!currentPhoto) return;

    try {
      const response = await fetch(currentPhoto, {
        mode: "cors", // или "no-cors" как fallback, но тогда blob будет opaque
      });

      if (!response.ok) throw new Error("Ошибка загрузки");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `photo-${currentIndex + 1}.jpg`; // или возьми из photo.filename
      // link.download = currentPhoto.split("/").pop() || "image.jpg";

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Не удалось скачать:", err);
      // Fallback: просто открыть ссылку (но откроется в таб)
      window.open(currentPhoto, "_blank");
      // или alert("Скачивание не удалось. Попробуйте сохранить изображение вручную.");
    }
  };

  // Замена фото
  const handleReplace = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Замени на свой endpoint
      const res = await replacePhoto(
        info.id,
        info.version,
        photos[currentIndex].path,
        formData,
        localCountry.iso.toUpperCase(),
      );
      // eslint-disable-next-line no-debugger
      debugger;

      // Обновляем массив
      const newPhotos = [...photos];
      newPhotos[currentIndex] = {
        original: res.product.photos[selected].original,
        path: res.product.photos[selected].path,
      };
      onReplacePhoto({
        index: currentIndex,
        isDraft: info.version === "draft",
        original: newPhotos[currentIndex].original,
        path: newPhotos[currentIndex].path,
      });
      setPhotos(newPhotos);

      // Можно сбросить viewed для этого слайда, если нужно
      // setViewed((prev) => prev.filter((v) => v !== selected));
    } catch (err) {
      console.error("Ошибка замены:", err);
      // message.error("Не удалось заменить фото");
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const triggerReplace = () => fileInputRef.current?.click();

  return (
    <div className={styles.wrapper}>
      <div className={styles.wrapperCarousel} style={{ position: "relative" }}>
        <Carousel
          beforeChange={(_, next) => {
            onChange(String(next));
          }}
          className={styles.carousel}
          arrows={photos.length > 1}
          nextArrow={<Button type="primary" icon={<img src={caretRight} alt="right" />} />}
          prevArrow={
            <Button type="primary" icon={<img src={caretRight} alt="left" style={{ transform: "rotate(180deg)" }} />} />
          }
          ref={(node) => {
            if (node) slider.current = node;
          }}
        >
          {photos.map((photo, i) => (
            <div key={photo.original}>
              {" "}
              <Image
                src={photo.original}
                alt={`slide ${i + 1}`}
                width={515}
                height={363}
                placeholder={
                  <div
                    style={{
                      width: 515,
                      height: 363,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#f5f5f5",
                    }}
                  >
                    <Spin size="large" />
                  </div>
                }
                style={{ objectFit: "contain" }}
                preview={false}
              />
            </div>
          ))}
        </Carousel>

        {/* Кнопки поверх карусели */}
        <div
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            display: "flex",
            gap: 8,
            zIndex: 10,
          }}
        >
          <CustomButton
            typeButton="tertiary"
            sizeButton="medium"
            onClick={triggerReplace}
            disabled={info.status !== ProductStatusEnum.PENDING}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <img src={ArrowsClockwise} alt="replace icon" />
              <div>Replace</div>
            </div>
          </CustomButton>
          <CustomButton sizeButton="medium" typeButton="tertiary" onClick={handleDownload}>
            <img src={DownloadSimple} alt="download" />
          </CustomButton>
        </div>

        {/* Скрытый input */}
        <input
          type="file"
          accept={`image/png,image/jpg,image/jpeg,${HEIC_ACCEPT}`}
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleReplace}
        />

        {/* Loader */}
        {isLoading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 20,
            }}
          >
            <Spin size="large" />
          </div>
        )}

        {/* Метка "viewed" */}
        {/* {viewed.includes(selected) && (
          <div className={styles.seen}>
            <img src={seen} alt="seen" />
            <span>viewed</span>
          </div>
        )} */}
      </div>

      <div className={styles.segmentWrapper}>
        <Segmented
          options={photos.map((photo, i) => ({
            label: (
              <img src={photo.original} width={86} height={61} alt={`thumb ${i}`} style={{ objectFit: "contain" }} />
            ),
            value: String(i),
          }))}
          value={selected}
          onChange={onChange}
          style={{ overflow: "auto" }}
        />
      </div>
    </div>
  );
};

export default CarouselComponent;
